import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../../components/ui/table";
import { 
  CreditCard, Calendar, Truck, Clock, CheckCircle2, AlertCircle, 
  ArrowRight, Crown, Zap, Loader2, History, ShoppingCart
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { formatCurrency, formatDate } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PLAN_ICONS = {
  starter: Truck,
  professional: Zap,
  enterprise: Crown
};

const PLAN_COLORS = {
  starter: "bg-blue-100 text-blue-800",
  professional: "bg-purple-100 text-purple-800",
  enterprise: "bg-amber-100 text-amber-800"
};

export default function CompanySubscription() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState({});
  const [vehicleLimit, setVehicleLimit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.company_id) {
      fetchData();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [subRes, plansRes, limitRes] = await Promise.all([
        axios.get(`${API}/subscriptions/company/${user.company_id}`, { headers, withCredentials: true }),
        axios.get(`${API}/subscriptions/plans`),
        axios.get(`${API}/vehicles/limit/${user.company_id}`, { headers, withCredentials: true })
      ]);
      
      setSubscription(subRes.data);
      setPlans(plansRes.data.plans);
      setVehicleLimit(limitRes.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel caricamento dei dati abbonamento");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "trial":
        return <Badge className="bg-blue-100 text-blue-800">Prova Gratuita</Badge>;
      case "active":
        return <Badge className="bg-green-100 text-green-800">Attivo</Badge>;
      case "expired":
        return <Badge className="bg-red-100 text-red-800">Scaduto</Badge>;
      case "cancelled":
        return <Badge className="bg-gray-100 text-gray-800">Cancellato</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const currentPlan = subscription?.subscription_plan ? plans[subscription.subscription_plan] : plans.starter;
  const PlanIcon = PLAN_ICONS[subscription?.subscription_plan || "starter"] || Truck;

  if (loading) {
    return (
      <DashboardLayout title="Il Mio Abbonamento">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!user?.company_id) {
    return (
      <DashboardLayout title="Il Mio Abbonamento">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-bold text-lg mb-2">Nessuna azienda registrata</h3>
            <p className="text-muted-foreground mb-4">
              Devi prima registrare la tua azienda per visualizzare abbonamento.
            </p>
            <Button onClick={() => window.location.href = "/company/profilo"}>
              Registra Azienda
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Il Mio Abbonamento">
      <div className="space-y-6" data-testid="company-subscription">
        {/* Current Plan Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Plan Info */}
          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Piano Attuale
                </CardTitle>
                {getStatusBadge(subscription?.subscription_status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-6">
                <div className={`p-4 ${subscription?.subscription_status === "active" ? "bg-primary" : "bg-primary/10"}`}>
                  <PlanIcon className={`h-8 w-8 ${subscription?.subscription_status === "active" ? "text-primary-foreground" : "text-primary"}`} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {currentPlan?.name || "Starter"}
                  </h3>
                  <p className="text-muted-foreground">
                    {currentPlan?.price ? `€${currentPlan.price}/mese` : "Piano base"}
                  </p>
                </div>
              </div>

              {/* Subscription Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 border border-border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    {subscription?.subscription_status === "trial" ? "Fine Prova" : "Scadenza"}
                  </div>
                  <p className="font-mono font-bold">
                    {subscription?.subscription_status === "trial" 
                      ? (subscription?.trial_end ? formatDate(subscription.trial_end) : "N/A")
                      : (subscription?.subscription_end ? formatDate(subscription.subscription_end) : "N/A")
                    }
                  </p>
                </div>
                <div className="p-4 bg-muted/50 border border-border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Clock className="h-4 w-4" />
                    Giorni Rimanenti
                  </div>
                  <p className="font-mono font-bold text-xl">
                    {subscription?.days_remaining || 0}
                  </p>
                </div>
              </div>

              {/* Warning if expired or expiring soon */}
              {subscription?.subscription_status === "expired" && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800">Abbonamento Scaduto</p>
                    <p className="text-sm text-red-600 mb-3">
                      Il tuo abbonamento e scaduto. Rinnova per continuare.
                    </p>
                    <Button onClick={() => navigate("/company/checkout")} size="sm">
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Rinnova Ora
                    </Button>
                  </div>
                </div>
              )}

              {subscription?.subscription_status === "trial" && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 flex items-start gap-3">
                  <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-800">Periodo di Prova</p>
                    <p className="text-sm text-blue-600 mb-3">
                      Stai usando il periodo di prova gratuita. Attiva un abbonamento per continuare.
                    </p>
                    <Button onClick={() => navigate("/company/checkout")} size="sm">
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Attiva Abbonamento
                    </Button>
                  </div>
                </div>
              )}

              {subscription?.days_remaining <= 7 && subscription?.days_remaining > 0 && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Abbonamento in Scadenza</p>
                    <p className="text-sm text-amber-600">
                      Il tuo abbonamento scade tra {subscription.days_remaining} giorni. Contatta amministrazione per il rinnovo.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vehicle Usage */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Utilizzo Veicoli</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <Truck className="h-16 w-16 text-primary/20" />
                  <span className="absolute inset-0 flex items-center justify-center font-mono font-bold text-lg">
                    {vehicleLimit?.current || 0}
                  </span>
                </div>
              </div>
              <div className="text-center mb-4">
                <p className="text-2xl font-bold font-mono">
                  {vehicleLimit?.current || 0} / {vehicleLimit?.max === "illimitati" ? "∞" : vehicleLimit?.max || 5}
                </p>
                <p className="text-sm text-muted-foreground">veicoli inseriti</p>
              </div>
              {vehicleLimit?.max !== "illimitati" && (
                <Progress 
                  value={(vehicleLimit?.current / vehicleLimit?.max) * 100} 
                  className="h-2"
                />
              )}
              {vehicleLimit && !vehicleLimit.can_add && (
                <p className="text-xs text-red-500 mt-2 text-center">
                  Limite raggiunto
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Features List */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Caratteristiche del Piano
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentPlan?.features?.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Available Plans */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Piani Disponibili
            </CardTitle>
            <CardDescription>
              Contatta l'amministrazione per cambiare piano o rinnovare l'abbonamento
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(plans).map(([planId, plan]) => {
                const Icon = PLAN_ICONS[planId] || Truck;
                const isCurrentPlan = (subscription?.subscription_plan || "starter") === planId;
                
                return (
                  <div 
                    key={planId}
                    className={`p-4 border ${isCurrentPlan ? "border-primary bg-primary/5" : "border-border"}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 ${isCurrentPlan ? "bg-primary" : "bg-muted"}`}>
                        <Icon className={`h-5 w-5 ${isCurrentPlan ? "text-primary-foreground" : "text-muted-foreground"}`} />
                      </div>
                      <div>
                        <h4 className="font-bold">{plan.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          €{plan.price}/mese
                        </p>
                      </div>
                      {isCurrentPlan && (
                        <Badge className="ml-auto bg-primary text-primary-foreground">Attuale</Badge>
                      )}
                    </div>
                    <ul className="space-y-1">
                      {plan.features?.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3 text-primary" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Payment History */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-muted-foreground" />
              <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                Storico Pagamenti
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {subscription?.payments?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Piano</TableHead>
                    <TableHead>Periodo</TableHead>
                    <TableHead>Metodo</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscription.payments.map((payment) => (
                    <TableRow key={payment.payment_id}>
                      <TableCell className="font-mono text-sm">
                        {formatDate(payment.payment_date)}
                      </TableCell>
                      <TableCell>
                        <Badge className={PLAN_COLORS[payment.plan_id] || "bg-gray-100"}>
                          {plans[payment.plan_id]?.name || payment.plan_id || "Standard"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(payment.period_start)} - {formatDate(payment.period_end)}
                      </TableCell>
                      <TableCell className="capitalize">
                        {payment.payment_method}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {formatCurrency(payment.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-8 text-center">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessun pagamento registrato</p>
                <p className="text-sm text-muted-foreground">
                  Stai utilizzando il periodo di prova gratuita
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
